import PropTypes from 'prop-types';
import React, { FC, useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';
import { Icon } from '@virtidev/toolbox';
import * as Styled from './MicBehaviorSelector.styled';
import { useTheme } from 'styled-components';
import { MicBehaviorContext } from '@vh/components/VHChatInputArea/MicBehaviorProvider.context';
import { useLocation } from 'react-router-dom';
import useFeature from '@core/components/LaunchDarkly';
import { virtualHumanTypeSelector } from '@vh/slices/VirtualHumanSlice';
import { useTranslation } from 'react-i18next';

/**
 * @type {FC<{disabled: boolean; solidButton: boolean;}>}
 */
const MicBehaviorSelector = ({ disabled, solidButton }) => {
  const { micBehavior, setMicBehavior, micActive } =
    useContext(MicBehaviorContext);
  const theme = useTheme();
  const handleClickPushToTalk = useCallback(() => {
    setMicBehavior('push_to_talk');
  }, [setMicBehavior]);
  const handleClickActivateWithVoice = useCallback(() => {
    setMicBehavior('open');
  }, [setMicBehavior]);
  const handleClickRecordAndSend = useCallback(() => {
    setMicBehavior('record_and_send');
  }, [setMicBehavior]);

  const recordAndSendMicrophoneOption = useFeature(
    'record-and-send-microphone-option'
  );
  const showRecordAndSend =
    micBehavior === 'record_and_send' || recordAndSendMicrophoneOption;

  const vhType = useSelector(virtualHumanTypeSelector);

  const { t } = useTranslation();

  return (
    <Styled.Wrapper>
      <Styled.MicBehaviorTooltip
        iconSize="18px"
        icon="dots"
        autoClose
        type="menu"
        disabled={disabled}
        $solid={solidButton}
      >
        <Styled.Menu>
          {/* // remove this check once record_and_send supported by branching */}
          {showRecordAndSend && vhType !== 'branching' && (
            <Styled.CardButton
              disabled={micActive}
              onClick={handleClickRecordAndSend}
            >
              {t('recordAndSend')}
              {micBehavior === 'record_and_send' && (
                <Icon color={theme.color.success} icon="tick" />
              )}
            </Styled.CardButton>
          )}
          <Styled.CardButton
            disabled={micActive}
            onClick={handleClickPushToTalk}
          >
            {t('pushToTalk')}
            {micBehavior === 'push_to_talk' && (
              <Icon color={theme.color.success} icon="tick" />
            )}
          </Styled.CardButton>
          <Styled.CardButton
            disabled={micActive}
            onClick={handleClickActivateWithVoice}
          >
            {t('voiceActivated')}
            {micBehavior === 'open' && (
              <Icon color={theme.color.success} icon="tick" />
            )}
          </Styled.CardButton>
          {micActive && (
            <div>{t('pleaseDeactivateMicrophoneToChangeBehavior')}</div>
          )}
        </Styled.Menu>
      </Styled.MicBehaviorTooltip>
    </Styled.Wrapper>
  );
};

MicBehaviorSelector.propTypes = { disabled: PropTypes.bool.isRequired };

export default MicBehaviorSelector;
